import "@blocknote/core/fonts/inter.css";
import "@blocknote/mantine/style.css";
import { BlockNoteSchema, defaultInlineContentSpecs, filterSuggestionItems } from "@blocknote/core";
import { BlockNoteView } from "@blocknote/mantine";
import {
	useCreateBlockNote,
	getDefaultReactSlashMenuItems,
	SuggestionMenuController
} from "@blocknote/react";
import { Fullscreen, FullscreenExit, CloudUpload } from "@mui/icons-material";
import { Box, IconButton, useTheme, Tooltip } from "@mui/material";
import { useEffect, useState } from "react";
import { t } from "i18next";
import { HTMLTEXT } from "../../../../../../contants";
import { useUploadFile } from "./useUploadFile";
import DialogSelectAsset from "./DialogSelectAsset";
import { FaCloudUploadAlt } from "react-icons/fa";
import { fr } from "@blocknote/core/locales";

const BlockNote = ({ value, handleChange, codeLanguage, codeLanguagePosibles }) => {
	const { palette } = useTheme();
	const [fullScreen, setFullScreen] = useState(false);
	const uploadFile = useUploadFile();
	const [uploadCloudAsset, setUploadCloudAsset] = useState(false);

	const schema = BlockNoteSchema.create({
		inlineContentSpecs: { ...defaultInlineContentSpecs }
	});
	const tempAnswerItemID = `temp_${Date.now()}`;
	const editorBlock = useCreateBlockNote({
		sideMenuDetection: "editor",
		schema,
		initialContent: [{ type: "paragraph" }],
		uploadFile: file => uploadFile(file, tempAnswerItemID, codeLanguage),
		dictionary: fr
	});

	const fetchData = async () => {
		let newValue = value || "";
		const newBlock = await editorBlock.tryParseHTMLToBlocks(newValue);
		editorBlock.replaceBlocks(editorBlock.document, newBlock);
	};

	useEffect(() => {
		fetchData();
	}, []);

	const onChange = async () => {
		const htmlContent = await editorBlock.blocksToHTMLLossy(editorBlock.document);
		handleChange(HTMLTEXT, htmlContent);
	};

	const toggleOpenFullscreen = () => {
		setFullScreen(prev => !prev);
	};

	const handleCloudUpload = (file, type) => {
		editorBlock.insertBlocks(
			[
				{
					type: type,
					props: {
						url: file?.url,
						caption: file?.name || "Fichier sans nom"
					}
				}
			],
			editorBlock.getSelection() || editorBlock.document[editorBlock.document.length - 1]
		);
	};

	// Composant personnalisé pour le menu Slash avec support des icônes et descriptions
	const CustomSuggestionMenu = props => {
		return (
			<div
				style={{
					position: "absolute",
					backgroundColor: "white",
					border: "1px solid #ccc",
					zIndex: 1000
				}}
			>
				{props.items.map((item, index) => (
					<div
						key={index}
						style={{ padding: "8px", cursor: "pointer" }}
						onClick={() => props.onItemClick(item)}
					>
						{item.icon && <span style={{ marginRight: "8px" }}>{item.icon}</span>}
						<div>
							{item.title}
							{item.subtext && (
								<div style={{ fontSize: "0.8em", color: "#666" }}>{item.subtext}</div>
							)}
						</div>
					</div>
				))}
			</div>
		);
	};

	// Élément de menu personnalisé avec icône de nuage et description
	const cloudUploadItem = editor => ({
		title: "Asset",
		subtext: t("SOURCES.DOCUMENT_DIALOG.uploadFrom"),
		onItemClick: () => setUploadCloudAsset(true),
		icon: <FaCloudUploadAlt />
	});

	const blockNoteViewBaseStyle = {
		borderWidth: 1,
		borderStyle: "solid",
		borderColor: palette.grey[400],
		borderRadius: 8,
		minHeight: 150
	};

	const blockNoteViewFullscreenStyle = {
		...blockNoteViewBaseStyle,
		minHeight: "100vh",
		position: "absolute",
		inset: 0,
		zIndex: 2
	};

	const iconStyle = {
		right: 0,
		top: 0,
		position: "absolute",
		zIndex: fullScreen ? 3 : 1,
		display: "flex",
		gap: 1
	};

	return (
		<Box position={fullScreen ? "inherit" : "relative"}>
			<Box sx={iconStyle}>
				<Tooltip title={t("SOURCES.DOCUMENT_DIALOG.uploadFrom")}>
					<IconButton
						onClick={() => {
							setUploadCloudAsset(true);
						}}
					>
						<CloudUpload />
					</IconButton>
				</Tooltip>

				{!fullScreen && (
					<IconButton onClick={toggleOpenFullscreen}>
						<Fullscreen />
					</IconButton>
				)}
				{fullScreen && (
					<IconButton onClick={toggleOpenFullscreen}>
						<FullscreenExit />
					</IconButton>
				)}
			</Box>
			<BlockNoteView
				style={fullScreen ? blockNoteViewFullscreenStyle : blockNoteViewBaseStyle}
				editor={editorBlock}
				onChange={onChange}
				lang="fr"
				theme="light"
			>
				<SuggestionMenuController
					triggerCharacter={"/"}
					getItems={async query => {
						const defaultItems = getDefaultReactSlashMenuItems(editorBlock);
						const mediaTitles = ["Image", "Video", "Audio", "File"];
						let maxMediaIndex = -1;
						for (let i = 0; i < defaultItems.length; i++) {
							if (mediaTitles.includes(defaultItems[i].title)) {
								maxMediaIndex = i;
							}
						}
						const allItems = [...defaultItems];
						if (maxMediaIndex >= 0) {
							allItems.splice(maxMediaIndex + 1, 0, cloudUploadItem(editorBlock));
						} else {
							allItems.push(cloudUploadItem(editorBlock));
						}
						return filterSuggestionItems(allItems, query);
					}}
					suggestionMenu={props => <CustomSuggestionMenu {...props} />}
				/>
			</BlockNoteView>
			{uploadCloudAsset && (
				<DialogSelectAsset
					onClose={() => setUploadCloudAsset(false)}
					onConfirmSelect={(file, type) => {
						handleCloudUpload(file, type);
						setUploadCloudAsset(false);
					}}
					codeLanguage={codeLanguage}
					codeLanguagePosibles={codeLanguagePosibles}
				/>
			)}
		</Box>
	);
};

export default BlockNote;
